import { track } from '@core/logging';
import { Button, ButtonType, Text, Heading } from '@soteria/design-system-ve';
import { Component, type ReactNode } from 'react';

import * as styles from './ModerationErrorBoundary.css';

interface ModerationErrorBoundaryProps {
  children: ReactNode;
}

interface ModerationErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorFallbackProps {
  error: Error | undefined;
  onRetry: () => void;
  onReload: () => void;
}

/**
 * Functional fallback UI component displayed when an error is caught
 */
function ErrorFallback({ error, onRetry, onReload }: ErrorFallbackProps) {
  return (
    <div className={styles.errorContainer}>
      <div className={styles.errorContent}>
        <Heading className={styles.errorTitle} level="h2">
          Something went wrong with the moderation system
        </Heading>

        <Text className={styles.errorDescription}>{error?.message || 'An unexpected error occurred while loading the moderation interface.'}</Text>

        <Text className={styles.errorHelp}>This error has been automatically reported. You can try the following actions:</Text>

        <div className={styles.errorActions}>
          <Button aria-label="Retry loading the moderation interface" buttonType={ButtonType.Primary} onClick={onRetry}>
            Try Again
          </Button>

          <Button aria-label="Reload the entire page" buttonType={ButtonType.Secondary} onClick={onReload}>
            Reload Page
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && error && (
          <details className={styles.errorDetails}>
            <summary className={styles.errorSummary}>Error Details (Development Only)</summary>
            <pre className={styles.errorStack}>{error.stack}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * Error boundary component for the Moderation domain
 * Catches JavaScript errors anywhere in the moderation component tree
 * and displays a fallback UI with recovery options
 *
 * Note: Error boundaries must use class components as React does not yet
 * support error boundary lifecycle methods in functional components.
 * The fallback UI is rendered via the functional ErrorFallback component.
 */
export class ModerationErrorBoundary extends Component<ModerationErrorBoundaryProps, ModerationErrorBoundaryState> {
  constructor(props: ModerationErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ModerationErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to monitoring service
    track('moderation_error_boundary_caught', {
      errorMessage: error.message,
      errorName: error.name,
      componentStack: errorInfo.componentStack ?? 'unknown',
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReload={this.handleReload} onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
